
import { defineComponent } from 'vue';
import MainLayout from '@/layout/MainLayout.vue';
import { useAuthStore } from '@/store/AuthStore';
import { useArtistsStore } from '@/store/ArtistsStore';
import {
  ArtistItem,
  SingleArtistData,
  SelectedExhibition,
  KeyCollection,
  Article,
} from '@/dataTypes/types';
import { useGalleryStore } from '@/store/GalleriesStore';
import { usePrivateSellersStore } from '@/store/PrivateSellersStore';
import { REGIONS, COUNTRIES } from '../constants';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import {
  getCountryName,
  getRegionName,
  getCountryId,
  getRegionId,
} from '@/helpers/regionAndCountryHelpers';
import {
  getCountriesForRegionName,
  sanitizeCountryForRegion,
} from '@/helpers/regionCountryFiltering';
import SpinnerLoader from '@/components/loader/SpinnerLoader.vue';
import Swal from 'sweetalert2';

export default defineComponent({
  components: { MainLayout, QuillEditor, SpinnerLoader },
  data() {
    const authStore = useAuthStore();
    const artistsStore = useArtistsStore();
    const galleryStore = useGalleryStore();
    const privateSeller = usePrivateSellersStore();
    const role = authStore.userRole;
    let id = +this.$route.params.id;
    if (!id) {
      const userInfo = authStore.getUserInfo();
      if (userInfo && userInfo.role === 'artist') {
        id = userInfo.id;
      }
    }
    return {
      id,
      artistsStore,
      galleryStore,
      privateSeller,
      authStore,
      role,
      lastName: '',
      firstName: '',
      regions: REGIONS,
      region: '',
      countries: COUNTRIES,
      country: '',
      imageUrl: '',
      about: '',
      title: '',
      url: '',
      artists: [] as ArtistItem[],
      artist: {} as SingleArtistData,
      exhibitions: [] as SelectedExhibition[],
      key_collections: [] as KeyCollection[],
      articles: [] as Article[],
      search: '',
      show: false,
      collectionsDate: '',
      collectionsMuseum: '',
      collectionsCountry: '',
      collectionsCity: '',
      exhibitionTitle: '',
      exhibitionsDate: '',
      exhibitionsType: '',
      exhibitionsLocation: '',
      exhibitionCountry: '',
      exhibitionCity: '',
      website: '',
      uploadedImageFile: '' as string,
      isLoading: true,
      imageFileName: '',
    };
  },
  async created() {
    if (this.authStore.isLoggedIn) {
      const authStore = useAuthStore();
      if (authStore.getUserInfo()) {
        if (
          this.id &&
          (this.authStore.userRole === 'gallery' || this.isPrivateSeller)
        ) {
          this.artists = await this.currentStore.fetchMyArtists();
          this.artist = await this.currentStore.fetchArtist(this.id);
          console.log(this.artist.image);
          this.firstName = this.artist.first_name || '';
          this.lastName = this.artist.last_name || '';
          this.imageUrl = this.artist.image?.original || '';
          this.country = this.getCountryName(this.artist.country_id) || '';
          this.region = this.getRegionName(this.artist.region_id) || '';
          this.about = this.artist.about || '';
          this.exhibitions = this.artist.selected_exhibitions || [];
          this.key_collections = this.artist.key_collections || [];
          this.articles = this.artist.articles || [];
          this.website = this.artist.website || '';
          this.isLoading = false;
        } else if (
          !this.id &&
          (this.authStore.userRole === 'gallery' || this.isPrivateSeller)
        ) {
          this.isLoading = false;
        }

        if (this.authStore.userRole === 'artist') {
          this.artist = await this.artistsStore.getInfo();
          this.firstName = this.artist.first_name || '';
          this.lastName = this.artist.last_name || '';
          this.imageUrl = this.artist.image.original || '';
          this.country = this.getCountryName(this.artist.country_id) || '';
          this.region = this.getRegionName(this.artist.region_id) || '';
          this.about = this.artist.about || '';
          this.exhibitions = this.artist.selected_exhibitions || [];
          this.key_collections = this.artist.key_collections || [];
          this.articles = this.artist.articles || [];
          this.website = this.artist.website || '';
          this.isLoading = false;
        }
      }
    }
  },
  computed: {
    isPrivateSeller() {
      return this.authStore.userRole === 'private_seller';
    },
    currentStore() {
      return this.isPrivateSeller ? this.privateSeller : this.galleryStore;
    },
    filteredCountries() {
      return getCountriesForRegionName(
        this.region,
        this.countries,
        this.regions
      );
    },
  },
  watch: {
    region() {
      this.country = sanitizeCountryForRegion(
        this.country,
        this.region,
        this.countries,
        this.regions
      );
    },
  },
  methods: {
    onFileChange(e: any) {
      const file = e.target.files[0];
      if (file) {
        this.uploadedImageFile = file;
        this.imageUrl = URL.createObjectURL(file);
        this.imageFileName = file.name;
      }
    },
    formatExhibitionType(exhibitionType: string): string {
      const typeArray = exhibitionType.split('_');
      const formattedType =
        typeArray[0].charAt(0).toUpperCase() + typeArray[0].substring(1);
      return formattedType;
    },
    addExhibition() {
      const exhibitionTitle = this.exhibitionTitle;
      const exhibitionDate = this.exhibitionsDate;
      const exhibitionType = this.exhibitionsType;
      const exhibitionLocation = this.exhibitionsLocation;
      const exhibitionCity = this.exhibitionCity;
      const exhibitionCountryID = this.exhibitionCountry;
      const exhibition = {
        title: exhibitionTitle,
        date: exhibitionDate,
        exhibition_type: exhibitionType,
        location: exhibitionLocation,
        city: exhibitionCity,
        country_id: +exhibitionCountryID,
      };
      this.exhibitions.push(exhibition);
      this.exhibitionTitle = '';
      this.exhibitionsDate = '';
      this.exhibitionsType = '';
      this.exhibitionsLocation = '';
      this.exhibitionCity = '';
      this.exhibitionCountry = '';
    },
    removeExhibition(title: string) {
      this.exhibitions = this.exhibitions.filter(
        (exhibition) => exhibition.title !== title
      );
    },
    addArticle() {
      const articlesTitle = this.title;
      const articlesURL = this.url;
      const article = {
        title: articlesTitle,
        url: articlesURL,
      };
      this.articles.push(article);
      this.title = '';
      this.url = '';
    },
    removeArticle(url: string) {
      this.articles = this.articles.filter((article) => article.url !== url);
    },
    addCollection() {
      const collectionsDate = this.collectionsDate;
      const collectionsMuseum = this.collectionsMuseum;
      const collectionsCity = this.collectionsCity;
      const collectionsCountryID = this.collectionsCountry;
      const collection = {
        date: collectionsDate,
        museum_name: collectionsMuseum,
        city: collectionsCity,
        country_id: +collectionsCountryID,
      };
      this.key_collections.push(collection);
      this.collectionsDate = '';
      this.collectionsMuseum = '';
      this.collectionsCity = '';
      this.collectionsCountry = '';
    },
    removeCollection(name: string) {
      this.key_collections = this.key_collections.filter(
        (collection) => collection.museum_name !== name
      );
    },
    getCountryName,
    getRegionName,
    getCountryId,
    async updateArtistData() {
      const exhibitionTitle = this.exhibitionTitle;
      const exhibitionDate = this.exhibitionsDate;
      const exhibitionType = this.exhibitionsType;
      const exhibitionLocation = this.exhibitionsLocation;
      const exhibitionCity = this.exhibitionCity;
      const exhibitionCountryID = this.exhibitionCountry;
      const collectionsDate = this.collectionsDate;
      const collectionsMuseum = this.collectionsMuseum;
      const collectionsCity = this.collectionsCity;
      const collectionsCountryID = this.collectionsCountry;
      const articlesTitle = this.title;
      const articlesURL = this.url;

      const dataObj = {
        first_name: this.firstName, // must be a string
        last_name: this.lastName, // must be a string
        about: this.about, // must be a string
        website: this.website, // must be a string
        region_id: String(getRegionId(this.region)), // must be a string
        country_id: String(getCountryId(this.country)), // must be a string
        selected_exhibitions: [
          // must be an array
          ...this.exhibitions,
          {
            title: exhibitionTitle,
            date: exhibitionDate,
            exhibition_type: exhibitionType,
            location: exhibitionLocation,
            city: exhibitionCity,
            country_id: exhibitionCountryID,
          },
        ],
        key_collections: [
          ...this.key_collections,
          // must be an array
          {
            date: collectionsDate,
            museum_name: collectionsMuseum,
            city: collectionsCity,
            country_id: collectionsCountryID,
          },
        ],
        articles: [
          // must be an array
          ...this.articles,
          {
            title: articlesTitle,
            url: articlesURL,
          },
        ],
      };

      const validationErrors: any[] = [];
      if (dataObj.first_name === '') {
        validationErrors.push('First name must have a value');
      }

      if (dataObj.last_name === '') {
        validationErrors.push('Last name must have a value');
      }

      if (dataObj.region_id === '') {
        validationErrors.push('Region must have a value');
      }

      if (dataObj.country_id === '') {
        validationErrors.push('Country must have a value');
      }

      if (validationErrors.length > 0) {
        Swal.fire({
          title: 'Error',
          text: validationErrors.join(', '),
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#000',
        });
        return;
      }

      // Convert to FormData
      const formData = new FormData();
      Object.entries(dataObj).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(key + '[]', JSON.stringify(item));
          });
        } else if (value !== null && value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      // Add image file if uploaded
      if (this.uploadedImageFile && this.imageFileName) {
        formData.append('image', this.uploadedImageFile, this.imageFileName);
      }

      // Call API to update artist data
      try {
        let response;
        if (this.id) {
          response = await this.currentStore.updateArtist(+this.id, formData);
        } else {
          response = await this.currentStore.createArtist(formData);
        }
        if (response?.success) {
          Swal.fire({
            title: 'Success',
            text: `Artist data ${this.id ? 'updated' : 'created'} successfully`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#000',
          });
        } else {
          Swal.fire({
            title: 'Error',
            text: (response as { error?: string }).error,
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#000',
          });
        }
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: (error as { message?: string }).message,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#000',
        });
      }
      // updateArtistInfo

      if (this.authStore.userRole === 'artist') {
        try {
          const response = await this.artistsStore.updateArtistInfo(formData);
          if (response) {
            Swal.fire({
              title: 'Success',
              text: 'Artist data updated successfully',
              icon: 'success',
              confirmButtonText: 'OK',
              confirmButtonColor: '#000',
            });
          }
        } catch (error) {
          Swal.fire({
            title: 'Error',
            text: (error as { message?: string }).message,
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#000',
          });
        }
      }
    },
  },
});
